import React from 'react';
import { Stack } from '../../Stack';
import { FieldWrapper } from '../FieldWrapper';
import { Checkbox } from '../FormItems/Checkbox';

type FieldWrapperProps = React.ComponentProps<typeof FieldWrapper>;
type StackProps = React.ComponentProps<typeof Stack>;

type CheckboxOption = {
  /** チェックボックスの値 */
  value: string;
  /** ラベルテキスト */
  label: string;
  /** 説明テキスト */
  description?: string;
  /** 無効状態 */
  disabled?: boolean;
};

type Props = {
  /** 選択肢の配列 */
  options: CheckboxOption[];
  /** 現在の選択値の配列 */
  value?: string[];
  /** 値が変更されたときのコールバック */
  onChange: (value: string[]) => void;
  /** 無効状態 */
  disabled?: boolean;
  /** チェックボックスの配置方向 */
  direction?: StackProps['direction'];
  /** チェックボックス間のgap */
  gap?: StackProps['gap'];
  /** FieldWrapperに渡すプロパティ */
  fieldWrapperProps: Omit<FieldWrapperProps, 'children'>;
};

const DEFAULT_DIRECTION = 'column';
const DEFAULT_GAP = 12;

export const CheckboxField: React.FC<Props> = ({
  options,
  value = [],
  onChange,
  disabled,
  direction = DEFAULT_DIRECTION,
  gap = DEFAULT_GAP,
  fieldWrapperProps: { htmlFor, labelType, labelText, beforeMessage, errorMessages },
}) => {
  const handleChange = (optionValue: string, checked: boolean) => {
    if (checked) {
      onChange([...value, optionValue]);
    } else {
      onChange(value.filter(v => v !== optionValue));
    }
  };

  return (
    <FieldWrapper
      htmlFor={htmlFor}
      labelType={labelType}
      labelText={labelText}
      beforeMessage={beforeMessage}
      errorMessages={errorMessages}
    >
      <Stack direction={direction} gap={gap}>
        {options.map(option => (
          <Checkbox
            key={option.value}
            value={option.value}
            label={option.label}
            description={option.description}
            checked={value.includes(option.value)}
            disabled={disabled || option.disabled}
            onCheckedChange={checked =>
              checked !== 'indeterminate' && handleChange(option.value, checked)
            }
          />
        ))}
      </Stack>
    </FieldWrapper>
  );
};
